import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Table pagination.
 *
 * Pass the full list in and render `pageItems`; this only controls which slice
 * is shown. Kept deliberately simple — these tables are read from a single
 * fetch, so paging happens client-side.
 */
export function usePagination(items, perPage = 15) {
  const [page, setPage] = React.useState(1);
  const list = items || [];
  const totalPages = Math.max(1, Math.ceil(list.length / perPage));

  // If the list shrinks (filter/search), don't strand the user on a dead page.
  React.useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const start = (page - 1) * perPage;
  const pageItems = list.slice(start, start + perPage);

  return { page, setPage, totalPages, pageItems, total: list.length, start, perPage };
}

export default function Pagination({ page, setPage, totalPages, total, start, perPage, label = "rows", compact = false }) {
  if (total === 0) return null;
  const from = start + 1;
  const to = Math.min(start + perPage, total);

  // Compact window of page numbers around the current page
  const windowSize = 5;
  let first = Math.max(1, page - Math.floor(windowSize / 2));
  const last = Math.min(totalPages, first + windowSize - 1);
  first = Math.max(1, last - windowSize + 1);
  const numbers = [];
  for (let i = first; i <= last; i++) numbers.push(i);

  return (
    <div
      className={compact
        ? "flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-line bg-pearl/40"
        : "flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t border-line bg-white"}
      data-testid={compact ? "pagination-top" : "pagination"}
    >
      <div className="text-[11px] text-ash">
        Showing <b className="text-ink">{from}–{to}</b> of <b className="text-ink">{total}</b> {label}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="p-1.5 border border-line text-ash hover:text-maroon hover:border-maroon disabled:opacity-30 disabled:hover:text-ash disabled:hover:border-line"
            aria-label="Previous page"
            data-testid="page-prev"
          >
            <ChevronLeft size={14}/>
          </button>

          {first > 1 && (
            <>
              <PageBtn n={1} page={page} setPage={setPage}/>
              {first > 2 && <span className="px-1 text-ash text-xs">…</span>}
            </>
          )}

          {numbers.map((n) => <PageBtn key={n} n={n} page={page} setPage={setPage}/>)}

          {last < totalPages && (
            <>
              {last < totalPages - 1 && <span className="px-1 text-ash text-xs">…</span>}
              <PageBtn n={totalPages} page={page} setPage={setPage}/>
            </>
          )}

          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="p-1.5 border border-line text-ash hover:text-maroon hover:border-maroon disabled:opacity-30 disabled:hover:text-ash disabled:hover:border-line"
            aria-label="Next page"
            data-testid="page-next"
          >
            <ChevronRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}

const PageBtn = ({ n, page, setPage }) => (
  <button
    onClick={() => setPage(n)}
    className={`min-w-[28px] px-2 py-1 text-xs border ${n === page ? "bg-maroon text-pearl border-maroon" : "border-line text-ash hover:border-maroon hover:text-maroon"}`}
    data-testid={`page-${n}`}
  >
    {n}
  </button>
);

/**
 * A second horizontal scrollbar shown ABOVE a wide table.
 *
 * Wide tables normally only have a scrollbar at the very bottom, so scrolling
 * sideways means scrolling the whole page down first. This mirrors the table's
 * horizontal scroll at the top, and the two stay in sync whichever one is used.
 *
 * Usage:
 *   <TableScroller>
 *     <table>...</table>
 *   </TableScroller>
 */
export function TableScroller({ children, className = "" }) {
  const topRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const spacerRef = React.useRef(null);
  const syncing = React.useRef(false);
  const [needed, setNeeded] = React.useState(false);

  // Keep the dummy spacer the same width as the real table so the top
  // scrollbar has the same range as the bottom one.
  React.useEffect(() => {
    const measure = () => {
      const body = bodyRef.current;
      if (!body || !spacerRef.current) return;
      spacerRef.current.style.width = `${body.scrollWidth}px`;
      setNeeded(body.scrollWidth > body.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (bodyRef.current) ro.observe(bodyRef.current);
    const mo = new MutationObserver(measure);
    if (bodyRef.current) mo.observe(bodyRef.current, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); mo.disconnect(); window.removeEventListener("resize", measure); };
  }, [children]);

  // Mirror scrolling in both directions without the two fighting each other.
  const mirror = (from, to) => {
    if (syncing.current) return;
    syncing.current = true;
    if (to.current && from.current) to.current.scrollLeft = from.current.scrollLeft;
    requestAnimationFrame(() => { syncing.current = false; });
  };

  return (
    <div className={className}>
      <div
        ref={topRef}
        onScroll={() => mirror(topRef, bodyRef)}
        className="overflow-x-auto overflow-y-hidden"
        style={{ height: needed ? 12 : 0 }}
        aria-hidden="true"
        data-testid="table-scroller-top"
      >
        <div ref={spacerRef} style={{ height: 1 }} />
      </div>

      <div
        ref={bodyRef}
        onScroll={() => mirror(bodyRef, topRef)}
        className="overflow-x-auto"
        data-testid="table-scroller-body"
      >
        {children}
      </div>
    </div>
  );
}
